// =============================================
// notesLib — types + data helpers for the Notes service
// =============================================
import { restoreSession } from '../../lib/session';
import { loadTable, addRow, saveRow, removeRow, NOTE_PK } from './notesApi';

// ---- Row shapes (loose — values may be string|number after JSON round-trip) ----
export interface NoteRow {
  note_id: number;
  user_id: number;
  account_owner_id?: number | null;
  title?: string | null;
  content_json?: string | null;   // TipTap JSON, stringified
  content_text?: string | null;   // flattened plain text
  is_important?: number;
  is_pinned?: number;
  color?: string | null;
  size_bytes?: number;
  tags?: string | null;              // comma-separated user labels
  keywords?: string | null;          // comma-separated searchable terms (local/AI)
  key_takeaway?: string | null;      // the one thing to remember
  review_at?: string | null;         // next spaced-repetition review (ISO) — null = no minder
  review_every_days?: number | null; // repeat interval in days (0 = one-off)
  review_note?: string | null;
  is_active?: number;
  is_deleted?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CategoryRow {
  category_id: number;
  user_id: number;
  parent_category_id?: number | null;
  category_name: string;
  category_path?: string;
  category_depth?: number;
  color?: string | null;
  icon?: string | null;
  sort_order?: number;
  is_active?: number;
  is_deleted?: number;
}

export interface LinkRow {
  link_id: number;
  note_id: number;
  category_id: number;
  user_id: number;
  is_deleted?: number;
}

/** The logged-in user's id (session). 0 when not authenticated. */
export function getCurrentUserId(): number {
  const s = restoreSession();
  return s ? Number(s.id) : 0;
}

// =============================================
// TipTap content helpers
// =============================================

/** An empty TipTap doc (a single empty paragraph). */
export function emptyDoc(): any {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

/** Flatten a TipTap JSON doc to plain text — for search + dashboard previews. */
export function flattenTipTap(doc: any): string {
  if (!doc || typeof doc !== 'object') return '';
  let out = '';
  const walk = (node: any) => {
    if (!node) return;
    if (node.type === 'text' && typeof node.text === 'string') out += node.text;
    if (Array.isArray(node.content)) {
      node.content.forEach((c: any) => walk(c));
      // Block-level nodes get a trailing newline so words don't run together.
      if (['paragraph', 'heading', 'listItem', 'blockquote'].includes(node.type)) out += '\n';
    }
  };
  walk(doc);
  return out.replace(/\n{3,}/g, '\n\n').trim();
}

/** Approximate stored size of a note in bytes (content + title). */
export function estimateBytes(contentJson: string, title?: string | null): number {
  const enc = (s: string) => (typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(s).length : s.length);
  return enc(contentJson || '') + enc(title || '');
}

/** Human-friendly bytes (B / KB / MB). */
export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** Parse a note's stored content_json into a TipTap doc (falls back to empty). */
export function parseContent(note: Pick<NoteRow, 'content_json'>): any {
  if (!note.content_json) return emptyDoc();
  try { return JSON.parse(note.content_json); } catch { return emptyDoc(); }
}

// =============================================
// Loading
// =============================================
export interface NotesBundle {
  notes: NoteRow[];
  categories: CategoryRow[];
  links: LinkRow[];
}

const active = (r: any) => r && r.is_deleted != 1; // eslint-disable-line eqeqeq

/** Load this user's notes, categories and category-links (server-scoped + cache). */
export async function loadNotesBundle(): Promise<NotesBundle> {
  const me = getCurrentUserId();
  const [notesRaw, catsRaw, linksRaw] = await Promise.all([
    loadTable('kv_entry').catch(() => []),
    loadTable('kv_category').catch(() => []),
    loadTable('kv_category_link').catch(() => []),
  ]);
  // In localStorage fallback mode rows aren't server-scoped, so filter to me.
  const mine = (r: any) => me === 0 || r.user_id == me; // eslint-disable-line eqeqeq
  return {
    notes: (notesRaw as NoteRow[]).filter(n => active(n) && mine(n)),
    categories: (catsRaw as CategoryRow[]).filter(c => active(c) && mine(c)),
    links: (linksRaw as LinkRow[]).filter(l => active(l) && mine(l)),
  };
}

// =============================================
// Category tree
// =============================================
export interface CategoryNode extends CategoryRow { children: CategoryNode[]; }

/** Build a parent→children tree from a flat category list, sorted by sort_order/name. */
export function buildCategoryTree(cats: CategoryRow[]): CategoryNode[] {
  const byId = new Map<number, CategoryNode>();
  cats.forEach(c => byId.set(c.category_id, { ...c, children: [] }));
  const roots: CategoryNode[] = [];
  byId.forEach(node => {
    const pid = node.parent_category_id;
    if (pid != null && byId.has(Number(pid))) byId.get(Number(pid))!.children.push(node);
    else roots.push(node);
  });
  const sort = (a: CategoryNode, b: CategoryNode) =>
    (a.sort_order || 0) - (b.sort_order || 0) || a.category_name.localeCompare(b.category_name);
  const sortRec = (nodes: CategoryNode[]) => { nodes.sort(sort); nodes.forEach(n => sortRec(n.children)); };
  sortRec(roots);
  return roots;
}

/** Full "Parent > Child > Leaf" breadcrumb label for a category. */
export function categoryBreadcrumb(cat: CategoryRow, all: CategoryRow[]): string {
  const parts = [cat.category_name];
  let cur: CategoryRow | undefined = cat;
  let depth = 0;
  while (cur && cur.parent_category_id != null && depth < 20) {
    // eslint-disable-next-line eqeqeq, no-loop-func
    const parent = all.find(c => c.category_id == cur!.parent_category_id);
    if (!parent) break;
    parts.unshift(parent.category_name);
    cur = parent;
    depth++;
  }
  return parts.join(' › ');
}

/** Flatten the tree into ordered [{cat, depth}] rows (for indented dropdowns/lists). */
export function flattenTree(nodes: CategoryNode[], depth = 0): { cat: CategoryNode; depth: number }[] {
  const out: { cat: CategoryNode; depth: number }[] = [];
  nodes.forEach(n => {
    out.push({ cat: n, depth });
    out.push(...flattenTree(n.children, depth + 1));
  });
  return out;
}

// =============================================
// Mutations
// =============================================

/** Create a new (blank) note for the current user. Returns the saved row. */
export async function createNote(partial: Partial<NoteRow> = {}): Promise<NoteRow> {
  const me = getCurrentUserId();
  const content = partial.content_json || JSON.stringify(emptyDoc());
  const row: Partial<NoteRow> = {
    user_id: me,
    account_owner_id: me,
    title: partial.title ?? '',
    content_json: content,
    content_text: partial.content_text ?? '',
    is_important: partial.is_important ?? 0,
    is_pinned: partial.is_pinned ?? 0,
    color: partial.color ?? null,
    size_bytes: estimateBytes(content, partial.title),
    is_active: 1,
    is_deleted: 0,
  };
  return (await addRow('kv_entry', row, NOTE_PK.kv_entry)) as NoteRow;
}

/** Persist edits to a note (title/content/flags). Recomputes size_bytes. */
export async function updateNote(noteId: number, updates: Partial<NoteRow>): Promise<NoteRow> {
  const patch: Partial<NoteRow> = { ...updates };
  if (updates.content_json != null) {
    patch.size_bytes = estimateBytes(updates.content_json, updates.title);
  }
  return (await saveRow('kv_entry', noteId, patch, NOTE_PK.kv_entry)) as NoteRow;
}

/** Soft-delete a note. */
export async function deleteNote(noteId: number): Promise<void> {
  await removeRow('kv_entry', noteId, NOTE_PK.kv_entry);
}

/** Create a category for the current user. */
export async function createCategory(name: string, parentId: number | null = null, color?: string): Promise<CategoryRow> {
  const me = getCurrentUserId();
  const row: Partial<CategoryRow> = {
    user_id: me,
    parent_category_id: parentId,
    category_name: name.trim(),
    category_depth: 0,
    color: color ?? null,
    sort_order: 0,
    is_active: 1,
    is_deleted: 0,
  };
  return (await addRow('kv_category', row, NOTE_PK.kv_category)) as CategoryRow;
}

/**
 * Reconcile a note's category links to exactly `categoryIds`:
 * add any missing, soft-delete any removed. Best-effort, parallelized.
 */
export async function setNoteCategories(noteId: number, categoryIds: number[], existing: LinkRow[]): Promise<void> {
  const me = getCurrentUserId();
  const current = existing.filter(l => l.note_id == noteId && l.is_deleted != 1); // eslint-disable-line eqeqeq
  const currentIds = new Set(current.map(l => Number(l.category_id)));
  const wanted = new Set(categoryIds.map(Number));

  const toAdd = Array.from(wanted).filter(id => !currentIds.has(id));
  const toRemove = current.filter(l => !wanted.has(Number(l.category_id)));

  await Promise.all([
    ...toAdd.map(category_id =>
      addRow('kv_category_link', { note_id: noteId, category_id, user_id: me, is_active: 1, is_deleted: 0 }, NOTE_PK.kv_category_link)
        .catch(() => null)),
    ...toRemove.map(l => removeRow('kv_category_link', l.link_id, NOTE_PK.kv_category_link).catch(() => null)),
  ]);
}

/** Rename / recolor / reparent a category. */
export async function updateCategory(categoryId: number, updates: Partial<CategoryRow>): Promise<CategoryRow> {
  return (await saveRow('kv_category', categoryId, updates, NOTE_PK.kv_category)) as CategoryRow;
}

/** Move a category under a new parent (null = root). */
export async function moveCategory(categoryId: number, parentId: number | null): Promise<CategoryRow> {
  return updateCategory(categoryId, { parent_category_id: parentId });
}

/** Soft-delete a category. */
export async function deleteCategory(categoryId: number): Promise<void> {
  await removeRow('kv_category', categoryId, NOTE_PK.kv_category);
}

/** Ids of a category and all its descendants. */
export function descendantCategoryIds(categoryId: number, cats: CategoryRow[]): number[] {
  const out = [categoryId];
  const kids = cats.filter(c => c.parent_category_id == categoryId); // eslint-disable-line eqeqeq
  kids.forEach(k => out.push(...descendantCategoryIds(k.category_id, cats)));
  return out;
}

/** How many entries are linked to a category (optionally including its subtree). */
export function entryCountForCategory(categoryId: number, links: LinkRow[], cats: CategoryRow[], deep = false): number {
  const ids = new Set(deep ? descendantCategoryIds(categoryId, cats) : [categoryId]);
  const noteIds = new Set<number>();
  links.forEach(l => { if (l.is_deleted != 1 && ids.has(Number(l.category_id))) noteIds.add(Number(l.note_id)); }); // eslint-disable-line eqeqeq
  return noteIds.size;
}

// =============================================
// Tags / keywords helpers (stored comma-separated)
// =============================================
export function parseList(s?: string | null): string[] {
  return (s || '').split(',').map(t => t.trim()).filter(Boolean);
}
export function joinList(arr: string[]): string {
  // de-dupe (case-insensitive), keep first-seen casing
  const seen = new Set<string>(); const out: string[] = [];
  arr.map(t => t.trim()).filter(Boolean).forEach(t => { const k = t.toLowerCase(); if (!seen.has(k)) { seen.add(k); out.push(t); } });
  return out.join(', ');
}

// =============================================
// Review minders (spaced repetition)
// =============================================
/** Is this entry due for review now? */
export function isReviewDue(note: NoteRow, at: Date): boolean {
  if (!note.review_at) return false;
  const d = new Date(note.review_at);
  return !isNaN(d.getTime()) && d.getTime() <= at.getTime();
}

/** Entries due for review (sorted oldest-due first). */
export function reviewDue(notes: NoteRow[], at: Date): NoteRow[] {
  return notes.filter(n => isReviewDue(n, at)).sort((a, b) => (a.review_at || '').localeCompare(b.review_at || ''));
}

/**
 * Mark an entry reviewed: advance review_at by review_every_days (repeat),
 * or clear it (one-off). Returns the patch to persist.
 */
export function nextReviewPatch(note: NoteRow): Partial<NoteRow> {
  const every = Number(note.review_every_days) || 0;
  if (every > 0) {
    const next = new Date(Date.now() + every * 24 * 60 * 60 * 1000);
    return { review_at: next.toISOString() };
  }
  return { review_at: null };
}
